import { tabletDiagnosticQueue, tabletDiagnosticRequest, tabletDiagnosticDeliveryBatch, type TabletDiagnosticEvent } from "../../../shared/production/tablet-diagnostics";
import type { LiveCounter } from "./live-counter";

const storagePrefix = "anl-production-tablet-diagnostics:v1:";
const queueLimit = 24;
const flushIntervalMs = 30_000;
const duplicateWindowMs = 10_000;

export type TabletDiagnosticContext = { terminalCode: string; sessionId: string; epoch?: number };
type Context = TabletDiagnosticContext;
type DiagnosticInput = Omit<TabletDiagnosticEvent, "id" | "occurredAt" | "counter" | "serverRequestId"> & { serverRequestId?: string | null };

let context: Context | null = null;
let counter: TabletDiagnosticEvent["counter"] = null;
let flushTimer: number | null = null;
let flushActive = false;
let globalListenersAttached = false;
let contextEpoch = 0;
const recent = new Map<string, number>();
const memoryQueues = new Map<string, TabletDiagnosticEvent[]>();
const hydratedScopes = new Set<string>();

function scopeKey(value: Context) {
  return `${value.terminalCode}:${value.sessionId}`;
}

function storageKey(value: Context) {
  return `${storagePrefix}${scopeKey(value)}`;
}

function storage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readQueue(value: Context): TabletDiagnosticEvent[] {
  const key = scopeKey(value);
  if (hydratedScopes.has(key)) return memoryQueues.get(key) ?? [];
  try {
    const saved = storage()?.getItem(storageKey(value));
    if (!saved) {
      hydratedScopes.add(key);
      memoryQueues.set(key, []);
      return [];
    }
    const parsed = tabletDiagnosticQueue.safeParse(JSON.parse(saved));
    if (!parsed.success) {
      storage()?.removeItem(storageKey(value));
      hydratedScopes.add(key);
      memoryQueues.set(key, []);
      return [];
    }
    hydratedScopes.add(key);
    memoryQueues.set(key, parsed.data);
    return parsed.data;
  } catch {
    hydratedScopes.add(key);
    memoryQueues.set(key, []);
    return [];
  }
}

function writeQueue(value: Context, events: TabletDiagnosticEvent[]) {
  const bounded = events.slice(-queueLimit);
  const key = scopeKey(value);
  hydratedScopes.add(key);
  if (bounded.length) memoryQueues.set(key, bounded); else memoryQueues.delete(key);
  try {
    const currentStorage = storage();
    if (!currentStorage) return;
    if (bounded.length) currentStorage.setItem(storageKey(value), JSON.stringify(bounded));
    else currentStorage.removeItem(storageKey(value));
  } catch {
    // Diagnostic delivery is best-effort. A full or unavailable local store must not affect production work.
  }
}

function safeCode(value: string | null | undefined) {
  return value && /^[A-Z0-9_]{1,80}$/u.test(value) ? value : null;
}

function safeRequestId(value: string | null | undefined) {
  return value && /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/u.test(value) ? value : null;
}

function currentCounter(now: number): TabletDiagnosticEvent["counter"] {
  if (!counter) return null;
  const measured = counter.measuredAt ? Date.parse(counter.measuredAt) : Number.NaN;
  return {
    ...counter,
    ageSeconds: Number.isFinite(measured) ? Math.min(86_400, Math.max(0, Math.floor((now - measured) / 1000))) : null,
  };
}

function scheduleFlush(delay = 0) {
  if (!context || flushTimer != null) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flushTabletDiagnostics();
  }, delay);
}

function attachGlobalListeners() {
  if (globalListenersAttached) return;
  globalListenersAttached = true;
  window.addEventListener("online", () => {
    recordTabletDiagnostic({ operation: "production.read", outcome: "RECOVERED", errorCode: "NETWORK_RECOVERED", durationMs: 0 });
    void flushTabletDiagnostics();
  });
  window.addEventListener("offline", () => {
    recordTabletDiagnostic({ operation: "production.read", outcome: "FAILURE", errorCode: "NETWORK_OFFLINE", durationMs: 0 });
  });
  window.addEventListener("error", () => {
    recordTabletDiagnostic({ operation: "production.read", outcome: "FAILURE", errorCode: "CLIENT_RUNTIME_ERROR", durationMs: 0 });
  });
  window.addEventListener("unhandledrejection", () => {
    recordTabletDiagnostic({ operation: "production.read", outcome: "FAILURE", errorCode: "CLIENT_UNHANDLED_REJECTION", durationMs: 0 });
  });
}

export function bindTabletDiagnostics(terminalCode: string, sessionId: string | null) {
  if (!sessionId) {
    const previous = context;
    contextEpoch += 1;
    context = null;
    counter = null;
    recent.clear();
    if (previous) writeQueue(previous, []);
    if (flushTimer != null) {
      window.clearTimeout(flushTimer);
      flushTimer = null;
    }
    return;
  }
  const next = { terminalCode, sessionId };
  const changed = !context || context.terminalCode !== next.terminalCode || context.sessionId !== next.sessionId;
  if (context && changed) {
    writeQueue(context, []);
  }
  if (changed) { contextEpoch += 1; recent.clear(); }
  context = next;
  if (changed) counter = null;
  attachGlobalListeners();
  scheduleFlush();
  if (flushTimer == null) scheduleFlush(flushIntervalMs);
}

export function clearTabletDiagnostics(terminalCode: string) {
  const current = context;
  if (current?.terminalCode === terminalCode) {
    contextEpoch += 1;
    context = null;
    writeQueue(current, []);
  }
  counter = null;
  recent.clear();
  if (flushTimer != null) {
    window.clearTimeout(flushTimer);
    flushTimer = null;
  }
}

export function setTabletDiagnosticCounter(value: LiveCounter | null) {
  if (!value) {
    counter = null;
    return;
  }
  counter = {
    configured: value.configured === true,
    source: value.source ?? null,
    quality: value.quality ?? null,
    measuredAt: value.measuredAt ?? null,
    ageSeconds: null,
  };
}

export function captureTabletDiagnosticContext(terminalCode: string, sessionId: string): TabletDiagnosticContext | null {
  if (!context || context.terminalCode !== terminalCode || context.sessionId !== sessionId) return null;
  return { ...context, epoch: contextEpoch };
}

export function recordTabletDiagnostic(input: DiagnosticInput, expectedContext?: TabletDiagnosticContext | null) {
  const current = context;
  if (!current || expectedContext === null) return;
  if (expectedContext?.epoch !== undefined && expectedContext.epoch !== contextEpoch) return;
  if (expectedContext && (expectedContext.terminalCode !== current.terminalCode || expectedContext.sessionId !== current.sessionId)) return;
  const now = Date.now();
  const errorCode = safeCode(input.errorCode);
  const measuredRequest = tabletDiagnosticRequest.safeParse(input.request);
  const request = measuredRequest.success ? measuredRequest.data : undefined;
  const dedupeKey = `${input.operation}:${input.outcome}:${errorCode ?? "none"}:${request?.attempt ?? "none"}`;
  const last = recent.get(dedupeKey);
  if (last != null && now - last < duplicateWindowMs) return;
  recent.set(dedupeKey, now);
  const event: TabletDiagnosticEvent = {
    id: crypto.randomUUID(),
    occurredAt: new Date(now).toISOString(),
    operation: input.operation,
    outcome: input.outcome,
    errorCode,
    serverRequestId: safeRequestId(input.serverRequestId),
    durationMs: Math.min(60_000, Math.max(0, Math.round(input.durationMs))),
    counter: currentCounter(now),
    ...(request ? { request } : {}),
  };
  writeQueue(current, [...readQueue(current), event]);
  scheduleFlush();
}

export async function flushTabletDiagnostics() {
  const current = context;
  if (!current || flushActive || !navigator.onLine) return;
  const epoch = contextEpoch;
  const queued = readQueue(current);
  if (!queued.length) return;
  flushActive = true;
  const batch = tabletDiagnosticDeliveryBatch(queued);
  if (!batch.length) { flushActive = false; return; }
  const controller = new AbortController();
  const deadline = window.setTimeout(() => controller.abort(), 5_000);
  try {
    const response = await fetch("/api/production/terminal/diagnostics", {
      method: "POST",
      credentials: "same-origin",
      signal: controller.signal,
      headers: { "content-type": "application/json", "x-production-terminal": current.terminalCode, "x-production-session-id": current.sessionId },
      body: JSON.stringify({ events: batch }),
    });
    if (contextEpoch !== epoch || context?.terminalCode !== current.terminalCode || context?.sessionId !== current.sessionId) return;
    if (response.ok) {
      const acknowledged = new Set(batch.map((event) => event.id));
      writeQueue(current, readQueue(current).filter((event) => !acknowledged.has(event.id)));
    } else if (response.status === 401 || response.status === 403 || response.status === 409) clearTabletDiagnostics(current.terminalCode);
  } catch {
    // Network failure is already represented by the original operation. Retain this small batch for a later online event.
  } finally {
    window.clearTimeout(deadline);
    flushActive = false;
    const next = context;
    if (next && readQueue(next).length) scheduleFlush(contextEpoch === epoch ? flushIntervalMs : 0);
  }
}
